import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/lib/api';

/**
 * Sign-in gate on the replay itself (Bir, 2026-07-30).
 *
 * Rule: reaching a replay by BROWSING the site requires an account; reaching it
 * by a link our own tooling generated does not.
 *
 * The gate lives on the ROUTE, not on the "Watch replay" button. There are two
 * doors into the same player — the button, and the `/view/:raceId` URL — and a
 * button-only guard is walked past by pasting the URL. Since that URL is
 * precisely what our Share button produces, a button-only gate would stop the
 * people clicking around and wave through everyone arriving by link, which is
 * backwards.
 */

/**
 * Film-room links carry camera state. Those are produced by our cut-sheet
 * tooling for Jarvis, who OPENS them to cut moments — the finished videos are
 * what reach YouTube, Instagram and TikTok, not the links. That makes the
 * audience small and known, so exempting them is a narrow hole rather than an
 * open door.
 *
 * `clip` is the marker because it is the one parameter no hand-typed or
 * shared-from-the-site URL carries: the viewer only ever writes it when a
 * specific camera is bound to the shot.
 */
export function isToolGeneratedReplayLink(search: string): boolean {
  return new URLSearchParams(search).get('clip') !== null;
}

/** Reject anything that isn't a same-site path. `//evil.com` is protocol-
 *  relative and browsers treat it as absolute, so it must not pass. */
function samePath(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//');
}

/**
 * Where to send them once they are signed in: the REGATTA page they came from,
 * so they pick the race again and watch it (Bir's call — the destination he
 * asked for is the regatta, not the race).
 *
 * Three sources, most trustworthy first:
 *
 *  1. `?back=` — already written by `RaceList` to aim the viewer's back button
 *     at the regatta. It is the same answer this needs, so the gate reuses it
 *     rather than adding a second parameter that could disagree with it.
 *  2. The slug in `/regatta/:slug/:div/race/:num`, for the routed variant that
 *     carries no `back`.
 *  3. The replay URL itself — the only sensible answer for a pasted link, where
 *     there is no regatta to go back to.
 *
 * Always a path, never a full URL: the API's `safeRedirect` drops anything that
 * doesn't start with a single `/`, and a dropped redirect lands the sailor on
 * the admin fallback instead of their race.
 */
export function replayReturnPath(pathname: string, search: string): string {
  const back = new URLSearchParams(search).get('back');
  if (samePath(back)) return back;

  const routed = pathname.match(/^(\/regatta\/[^/]+)\/[^/]+\/race\/[^/]+$/);
  if (routed) return routed[1];

  return `${pathname}${search}`;
}

export function RequireReplayAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();
  const { raceId } = useParams();

  // Signed in, or arriving by a tool-generated film-room link: always through.
  const exempt = isAuthenticated || isToolGeneratedReplayLink(location.search);

  // Otherwise a PUBLIC race opens to anyone with the link, no account. Only the
  // `/view/:raceId` door carries a raceId to check; the browsed
  // `/regatta/:slug/:div/race/:num` door has none, so it keeps requiring an
  // account (Bir's rule: browsing the site needs sign-in; a shared public link
  // does not). The check is unauthenticated — GET /races/:raceId/access.
  const [publicCheck, setPublicCheck] =
    useState<'checking' | 'public' | 'gated'>('checking');

  useEffect(() => {
    if (exempt || !raceId) return;
    let cancelled = false;
    setPublicCheck('checking');
    api
      .get<{ public: boolean }>(`/races/${raceId}/access`, { silent: true })
      .then((res) => { if (!cancelled) setPublicCheck(res.public ? 'public' : 'gated'); })
      .catch(() => { if (!cancelled) setPublicCheck('gated'); });
    return () => { cancelled = true; };
  }, [exempt, raceId]);

  if (exempt) return <>{children}</>;

  if (raceId) {
    // Hold on the unauthenticated check rather than flashing the login page:
    // a public race must never briefly redirect before we know it is public.
    if (publicCheck === 'checking') {
      return (
        <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
          Loading…
        </div>
      );
    }
    if (publicCheck === 'public') return <>{children}</>;
  }

  const target = replayReturnPath(location.pathname, location.search);

  return <Navigate to={`/login?redirect=${encodeURIComponent(target)}`} replace />;
}
